import { Outlet, Route, Routes } from 'react-router';
import { AdminPage } from './features/admin/AdminPage';
import { AdminLoginPage } from './features/admin/AdminLoginPage';
import { AdminSetupPage } from './features/admin/AdminSetupPage';
import { NewTenantPage } from './features/admin/NewTenantPage';
import { TenantSecretsPage } from './features/admin/TenantSecretsPage';
import { TenantNumbersPage } from './features/admin/TenantNumbersPage';
import { LoginPage } from './features/auth/LoginPage';
import { IndexRedirect } from './features/auth/IndexRedirect';
import { OnboardingPage } from './features/onboarding/OnboardingPage';
import { WorkspaceShell } from './features/workspace/WorkspaceShell';
import { WorkspaceOverview } from './features/workspace/WorkspaceOverview';
import { JobsPage } from './features/jobs/JobsPage';
import { InboxPage } from './features/inbox/InboxPage';
import { LeadsPage } from './features/leads/LeadsPage';
import { CallsPage } from './features/calls/CallsPage';
import { AppointmentsPage } from './features/appointments/AppointmentsPage';
import { AvailabilityRulesPage } from './features/settings/availability/AvailabilityRulesPage';
import { BillingPage } from './features/billing/BillingPage';
import { BookingPage } from './features/booking/BookingPage';
import { RequireAuth } from './components/RequireAuth';
import { RequirePlatformAdmin } from './components/RequirePlatformAdmin';
import { NotFound } from './components/NotFound';

/**
 * SPA route table — the Phase 1 slice of ARCHITECTURE_PLAN §10:
 *   /            → dispatcher (first workspace | /onboarding | /login)
 *   /login       → email → 6-digit code → session (public)
 *   /onboarding  → create workspace (session required)
 *   /w/:slug     → workspace shell (session + membership required)
 *   *            → 404
 *
 * Later phases append /w/:slug/inbox|leads|calls|appointments|contacts|
 * settings|jobs, /book/:slug (Phase 4), and /admin as their backends land (§14).
 * Providers (QueryClient + Router) are wired in `app/main.tsx`.
 */
export function App(): React.JSX.Element {
  return (
    <Routes>
      <Route element={<RootLayout />}>
        <Route path="/" element={<IndexRedirect />} />
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/onboarding"
          element={
            <RequireAuth>
              <OnboardingPage />
            </RequireAuth>
          }
        />
        <Route
          path="/w/:slug"
          element={
            <RequireAuth>
              <WorkspaceShell />
            </RequireAuth>
          }
        >
          <Route index element={<WorkspaceOverview />} />
          <Route path="inbox" element={<InboxPage />} />
          <Route path="leads" element={<LeadsPage />} />
          <Route path="calls" element={<CallsPage />} />
          <Route path="jobs" element={<JobsPage />} />
          <Route path="appointments" element={<AppointmentsPage />} />
          <Route path="settings/availability" element={<AvailabilityRulesPage />} />
          <Route path="settings/billing" element={<BillingPage />} />
        </Route>
        <Route path="/book/:slug" element={<BookingPage />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin/setup" element={<AdminSetupPage />} />
        <Route
          path="/admin"
          element={
            <RequirePlatformAdmin>
              <AdminPage />
            </RequirePlatformAdmin>
          }
        />
        <Route
          path="/admin/tenants/new"
          element={
            <RequirePlatformAdmin>
              <NewTenantPage />
            </RequirePlatformAdmin>
          }
        />
        <Route
          path="/admin/tenants/:id/secrets"
          element={
            <RequirePlatformAdmin>
              <TenantSecretsPage />
            </RequirePlatformAdmin>
          }
        />
        <Route
          path="/admin/tenants/:id/numbers"
          element={
            <RequirePlatformAdmin>
              <TenantNumbersPage />
            </RequirePlatformAdmin>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

/** Root layout: base page chrome shared by every route. */
function RootLayout(): React.JSX.Element {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <Outlet />
    </div>
  );
}
